// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: where the notification paths hand a message to the in-app pop-ups (design/assistant-everywhere
// t1, k1, t4). Notification (Konvo chats, mentions, Teams) and KonvoWhatsApp offer a message here
// before they would show an OS notification; the pop-up store registers itself when it loads. No
// runtime imports on purpose: both notification paths and their tests pull this in, and with no
// store registered every offer is declined, so they behave exactly as before.

import type {Channel} from '@app/features/channel/models/Channel';
import type {WhatsAppChat, WhatsAppMessage} from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import type {Message} from '@app/features/messaging/models/MessagingMessage';

export type InAppPopupOffer =
	| {kind: 'channel'; channel: Channel; message: Message; mentioned: boolean; focused: boolean}
	| {kind: 'whatsapp'; chat: WhatsAppChat; message: WhatsAppMessage; focused: boolean};

/**
 * shown: a pop-up has it (or holds it while you type); play the sound, skip the OS notification.
 * quiet: Konvo's reply to something you are waiting on elsewhere; play the sound, show nothing.
 * declined: not for a pop-up (off, in a call, window in the background); notify as before.
 */
export type InAppPopupResult = 'shown' | 'quiet' | 'declined';

interface InAppPopupHandlers {
	offer: (offer: InAppPopupOffer) => InAppPopupResult;
	/** The conversation was read somewhere: its pop-up goes. */
	read: (channelId: string) => void;
}

let handlers: InAppPopupHandlers | null = null;
const ownMessageAt = new Map<string, number>();

export function registerInAppPopupHandlers(next: InAppPopupHandlers): void {
	handlers = next;
}

export function offerInAppPopup(offer: InAppPopupOffer): InAppPopupResult {
	try {
		return handlers?.offer(offer) ?? 'declined';
	} catch {
		return 'declined';
	}
}

export function noteInAppPopupRead(channelId: string): void {
	handlers?.read(channelId);
}

/** You sent something in this chat (any window, any surface): Konvo's reply to it is not news. */
export function noteOwnMessage(channelId: string, at: number = Date.now()): void {
	ownMessageAt.set(channelId, at);
}

export function lastOwnMessageAt(channelId: string): number {
	return ownMessageAt.get(channelId) ?? 0;
}
